/* Kincmo floating chat V31.0 — pre-initialize chat behind the PiP confirmation screen. */
(() => {
  'use strict';

  window.__kincmoInboxPip?.destroy?.();

  const launcher = () => window.__kincmoInboxLauncherController;
  const PARAM = 'kincmo_floating_chat';
  const CONFIRM_PARAM = 'kincmo_floating_confirm';
  const NEXT_PARAM = 'kincmo_floating_next';
  const POPUP_NAME = 'kincmo-floating-chat';
  const POPUP_WIDTH = 520;
  const POPUP_HEIGHT = 760;
  const searchParams = new URLSearchParams(location.search);
  const isPopupMode = searchParams.get(PARAM) === '1' || Boolean(window.__kincmoFetchedPipMode);
  const isConfirmMode = searchParams.get(CONFIRM_PARAM) === '1';

  let destroyed = false;
  let popup = null;
  let dismissed = false;
  let pollId = 0;
  let openAttempts = 0;
  let confirmPrewarmReady = false;
  let confirmPrewarmTimer = 0;
  let confirmPrewarmAttempts = 0;

  function desktop() {
    return window.innerWidth > 749 &&
      !window.matchMedia('(pointer: coarse)').matches &&
      !(/iPad|iPhone|iPod|Android/i.test(navigator.userAgent));
  }


  function paintInstantConfirmShell(win, onKeep) {
    if (!win || win.closed) return;
    try {
      const doc = win.document;
      doc.open();
      doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Kincmo chat</title>
<style>
html,body{margin:0;width:100%;height:100%;overflow:hidden;background:#f7f7f7;color:#1f1f1f;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
main{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;background:#f7f7f7}
.card{width:min(100%,400px);background:#fff;border:1px solid #ece8e3;border-radius:14px;padding:28px 24px;box-sizing:border-box;box-shadow:0 8px 28px rgba(0,0,0,.12)}
h1{margin:0 0 14px;font:700 22px/1.25 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;letter-spacing:0;text-transform:none;white-space:nowrap;word-break:normal;overflow-wrap:normal}
p{margin:0 0 24px;font:400 15px/1.45 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;letter-spacing:0;text-transform:none;color:#4f4a46}
.actions{display:grid;gap:12px}
button{appearance:none;width:100%;min-height:48px;border-radius:10px;border:1px solid #ddd3cc;font:600 15px/1 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.primary{background:#71351f;color:#fff;border-color:#71351f}.secondary{background:#f1f1f1;color:#222}
</style>
</head>
<body>
<main><section class="card">
<h1>Keep chatting in this window?</h1>
<p>Your Kincmo conversation stays available while you switch tabs or use another app. Keep the original Kincmo tab open.</p>
<div class="actions"><button class="primary" type="button">Keep chatting here</button><button class="secondary" type="button">Close window</button></div>
</section></main>
</body>
</html>`);
      doc.close();
      const keep = doc.querySelector('.primary');
      let accepted = false;
      keep.addEventListener('click', () => {
        if (accepted) return;
        accepted = true;
        keep.disabled = true;
        keep.setAttribute('aria-busy', 'true');
        keep.textContent = 'Opening chat…';
        onKeep();
      });
      doc.querySelector('.secondary').addEventListener('click', () => win.close());
    } catch (_) {}
  }

  function popupFeatures() {
    const left = Math.max(0, Math.round((window.screen.availWidth || screen.width) - POPUP_WIDTH - 24));
    const top = Math.max(0, Math.round(((window.screen.availHeight || screen.height) - POPUP_HEIGHT) / 2));
    return [
      'popup=yes',
      `width=${POPUP_WIDTH}`,
      `height=${POPUP_HEIGHT}`,
      `left=${left}`,
      `top=${top}`,
      'resizable=yes',
      'scrollbars=no'
    ].join(',');
  }

  function popupUrl() {
    const url = new URL(location.href);
    url.pathname = window.Shopify?.routes?.root || '/';
    url.searchParams.set('view', 'kincmo-chat');
    url.searchParams.delete('kincmo_floating_accepted');
    url.searchParams.set(PARAM, '1');
    url.searchParams.delete(CONFIRM_PARAM);

    // Theme Editor / draft preview: a top-level popup does not inherit the
    // editor iframe's preview routing. Explicitly keep the current theme ID so
    // the popup loads the exact same draft theme instead of the live theme.
    const themeId = window.Shopify?.theme?.id;
    const inThemeEditor = Boolean(window.Shopify?.designMode) || url.searchParams.get('ose') === 'false';
    if (themeId && inThemeEditor) {
      url.searchParams.set('preview_theme_id', String(themeId));
    }

    // Prevent opening a nested floating window from an already floating window.
    url.hash = '';
    return url.href;
  }

  function popupConfirmUrl() {
    const finalChatUrl = popupUrl();
    const url = new URL(finalChatUrl);

    url.searchParams.delete(PARAM);
    url.searchParams.set(CONFIRM_PARAM, '1');
    // Persist the exact final destination while we are still in the source tab.
    // This avoids recomputing preview/theme routing inside the confirmation window.
    url.searchParams.set(NEXT_PARAM, finalChatUrl);

    const themeId = window.Shopify?.theme?.id;
    const inThemeEditor = Boolean(window.Shopify?.designMode) || url.searchParams.get('ose') === 'false';
    if (themeId && inThemeEditor) {
      url.searchParams.set('preview_theme_id', String(themeId));
    }

    url.hash = '';
    return url.href;
  }

  function walkRoots(root, callback) {
    if (!root) return false;
    if (callback(root)) return true;
    const elements = root.querySelectorAll?.('*') || [];
    for (const element of elements) {
      if (element.shadowRoot && walkRoots(element.shadowRoot, callback)) return true;
    }
    return false;
  }

  function clickOfficialChatLauncher() {
    const hosts = launcher()?.getHosts?.() || Array.from(document.querySelectorAll('shopify-chat, shopify-agent'));

    for (const host of hosts) {
      if (!host?.isConnected) continue;
      if (launcher()?.isHostOpen?.(host)) return true;

      let clicked = false;
      if (host.shadowRoot) {
        walkRoots(host.shadowRoot, (root) => {
          const candidates = [
            '.chat-toggle:not([aria-expanded="true"])',
            '[data-testid*="chat" i][role="button"]',
            'button[aria-label*="chat" i]',
            'button[title*="chat" i]',
            'button[aria-expanded="false"]',
            '[role="button"][aria-expanded="false"]'
          ];
          for (const selector of candidates) {
            const control = root.querySelector?.(selector);
            if (control && control.getClientRects().length) {
              control.click();
              clicked = true;
              return true;
            }
          }
          return false;
        });
      }
      if (clicked) return true;
    }

    // Last-resort: click the deepest element under the normal bottom-right launcher position.
    try {
      let root = document;
      let target = root.elementFromPoint(window.innerWidth - 28, window.innerHeight - 28);
      while (target?.shadowRoot) {
        const next = target.shadowRoot.elementFromPoint(window.innerWidth - 28, window.innerHeight - 28);
        if (!next || next === target) break;
        target = next;
      }
      if (target instanceof HTMLElement) {
        target.click();
        return true;
      }
    } catch (_) {}

    return false;
  }

  function installFloatingLifecycle() {
    let stopped = false;
    let closedChecks = 0;
    let channel = null;
    const closePopup = () => {
      if (stopped) return;
      cleanup();
      // When the dedicated chat page is rendered inside Document PiP, ask the
      // PiP parent to close. window.close() cannot close an iframe.
      if (window.parent && window.parent !== window) {
        try {
          window.parent.postMessage({ type: 'kincmo-floating-chat-close' }, location.origin);
          return;
        } catch (_) {}
      }
      window.close();
    };
    const onClick = (event) => {
      const path = event.composedPath?.() || [];
      if (!path.some((node) => /^(SHOPIFY-CHAT|SHOPIFY-AGENT)$/.test(node?.tagName || ''))) return;
      if (path.some((node) => node?.matches?.('.chat-app--close-button, .chat-toggle[aria-expanded="true"]'))) closePopup();
    };
    const checkCollapsed = () => {
      if (stopped || destroyed || !document.documentElement.classList.contains('kincmo-floating-chat-ready')) return;
      if (launcher()?.isChatOpen?.()) closedChecks = 0;
      else if (++closedChecks >= 2) closePopup();
    };
    try {
      channel = new BroadcastChannel('kincmo-floating-lifecycle');
      channel.onmessage = (event) => {
        if (event.data?.type === 'storefront-active') closePopup();
      };
    } catch (_) {}
    document.addEventListener('click', onClick, true);
    document.addEventListener('kincmo:inbox-state', checkCollapsed);
    const timer = window.setInterval(checkCollapsed, 200);
    function cleanup() {
      stopped = true;
      window.clearInterval(timer);
      channel?.close();
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('kincmo:inbox-state', checkCollapsed);
      window.removeEventListener('pagehide', cleanup);
    }
    window.addEventListener('pagehide', cleanup, { once: true });
  }

  function installPopupPresentation(prewarmed = false) {
    document.documentElement.classList.add('kincmo-floating-chat-window');

    const style = document.createElement('style');
    style.id = 'kincmo-floating-chat-window-style';
    style.textContent = `
      html.kincmo-floating-chat-window,
      html.kincmo-floating-chat-window body {
        margin:0 !important;
        width:100% !important;
        height:100% !important;
        background:#fff !important;
        overflow:hidden !important;
      }

      /* Hide the storefront completely in popup mode.  We reveal only the
         ancestor chain that actually contains Shopify Inbox. */
      html.kincmo-floating-chat-window body > * {
        visibility:hidden !important;
      }
      html.kincmo-floating-chat-window body > script,
      html.kincmo-floating-chat-window body > style,
      html.kincmo-floating-chat-window body > link {
        display:none !important;
      }
      html.kincmo-floating-chat-window .kincmo-chat-keep,
      html.kincmo-floating-chat-window .kincmo-chat-keep * {
        visibility:visible !important;
      }

      html.kincmo-floating-chat-window #kincmo-inbox-pip,
      html.kincmo-floating-chat-window #kincmo-inbox-custom-launcher {
        display:none !important;
      }

      /* Keep Shopify Inbox behind the boot shield while it mounts/animates.
         This prevents its gray backdrop from flashing before the chat is ready. */
      html.kincmo-floating-chat-window shopify-chat,
      html.kincmo-floating-chat-window shopify-agent {
        visibility:visible !important;
        z-index:2147483646 !important;
      }
      html.kincmo-floating-chat-window.kincmo-floating-chat-ready shopify-chat,
      html.kincmo-floating-chat-window.kincmo-floating-chat-ready shopify-agent {
        z-index:2147483647 !important;
      }

      #kincmo-floating-chat-error {
        position:fixed !important;
        inset:0 !important;
        display:flex !important;
        align-items:center !important;
        justify-content:center !important;
        z-index:2147483647 !important;
        background:#fff !important;
        color:#6b625d !important;
        font:15px/1.4 system-ui,sans-serif !important;
        visibility:visible !important;
      }
    `;
    document.head.appendChild(style);

    function inboxHosts() {
      const fromController = launcher()?.getHosts?.() || [];
      const fromDom = Array.from(document.querySelectorAll('shopify-chat, shopify-agent'));
      return Array.from(new Set([...fromController, ...fromDom])).filter((node) => node?.isConnected);
    }

    function revealInboxAncestors() {
      const hosts = inboxHosts();
      for (const host of hosts) {
        let node = host;
        while (node && node !== document.body) {
          if (node.classList) node.classList.add('kincmo-chat-keep');
          node = node.parentElement;
        }
        if (host.classList) host.classList.add('kincmo-chat-keep');
      }
      return hosts;
    }

    function ensureChatOpen() {
      if (destroyed) return;

      const hosts = revealInboxAncestors();
      const open = Boolean(launcher()?.isChatOpen?.()) || hosts.some((host) => launcher()?.isHostOpen?.(host));

      if (open) {
        document.documentElement.classList.add('kincmo-floating-chat-ready');
        // Keep Shopify Inbox hidden for one additional paint after it reports
        // open. This prevents the launch button / opening animation from ever
        // becoming visible between the boot shield and the finished chat UI.
        requestAnimationFrame(() => {
          document.documentElement.classList.remove('kincmo-floating-chat-boot');
          window.focus();
        });
        return;
      }

      clickOfficialChatLauncher();
      openAttempts += 1;
      if (openAttempts < 70) {
        pollId = window.setTimeout(ensureChatOpen, openAttempts < 15 ? 60 : 120);
      } else {
        document.documentElement.classList.remove('kincmo-floating-chat-boot');
        const error = document.createElement('div');
        error.id = 'kincmo-floating-chat-error';
        error.textContent = 'Could not open chat automatically. Please reload this window.';
        document.body.appendChild(error);
      }
    }

    // Shopify Inbox may mount after DOMContentLoaded, so continuously discover
    // the real host while the popup is starting.
    const observer = new MutationObserver(() => revealInboxAncestors());
    observer.observe(document.documentElement, { childList: true, subtree: true });

    const oldDestroy = window.__kincmoInboxPip?.destroy;
    window.__kincmoInboxPip = {
      version: '21.10.2-popup-window',
      destroy() {
        destroyed = true;
        window.clearTimeout(pollId);
        observer.disconnect();
        oldDestroy?.();
      }
    };

    pollId = window.setTimeout(ensureChatOpen, prewarmed ? 0 : 50);
  }

  function installConfirmPage() {
    document.documentElement.classList.add('kincmo-floating-chat-confirm-window');

    const earlyPanel = document.getElementById('kincmo-floating-chat-early-confirm');
    let panel = earlyPanel;
    let style = null;

    if (!panel) {
      style = document.createElement('style');
      style.id = 'kincmo-floating-chat-confirm-style';
      style.textContent = `
        html.kincmo-floating-chat-confirm-window,
        html.kincmo-floating-chat-confirm-window body {
          margin:0 !important;
          width:100% !important;
          height:100% !important;
          overflow:hidden !important;
          background:#f7f7f7 !important;
        }
        html.kincmo-floating-chat-confirm-window body > * { display:none !important; }
        html.kincmo-floating-chat-confirm-window #kincmo-floating-chat-confirm { display:flex !important; }
        #kincmo-floating-chat-confirm {
          position:fixed !important; inset:0 !important; z-index:2147483647 !important;
          align-items:center !important; justify-content:center !important; padding:24px !important;
          background:#f7f7f7 !important; color:#1f1f1f !important;
          font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif !important;
        }
        #kincmo-floating-chat-confirm .card {
          width:min(100%,400px) !important; background:#fff !important;
          border:1px solid #ece8e3 !important; border-radius:14px !important;
          padding:28px 24px !important; box-shadow:0 8px 28px rgba(0,0,0,.12) !important;
        }
        #kincmo-floating-chat-confirm h1 { margin:0 0 14px !important; font:700 22px/1.25 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif !important; letter-spacing:0 !important; text-transform:none !important; white-space:nowrap !important; word-break:normal !important; overflow-wrap:normal !important; }
        #kincmo-floating-chat-confirm p { margin:0 0 24px !important; font:400 15px/1.45 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif !important; letter-spacing:0 !important; text-transform:none !important; color:#4f4a46 !important; }
        #kincmo-floating-chat-confirm .actions { display:grid !important; gap:12px !important; }
        #kincmo-floating-chat-confirm button {
          appearance:none !important; width:100% !important; min-height:48px !important;
          border-radius:10px !important; border:1px solid #ddd3cc !important;
          font:600 15px/1 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif !important;
          cursor:pointer !important;
        }
        #kincmo-floating-chat-confirm [data-keep] { background:#71351f !important; color:#fff !important; border-color:#71351f !important; }
        #kincmo-floating-chat-confirm [data-close] { background:#f1f1f1 !important; color:#222 !important; }
        #kincmo-floating-chat-confirm button:disabled { cursor:default !important; opacity:.72 !important; }
      `;
      document.head.appendChild(style);

      panel = document.createElement('main');
      panel.id = 'kincmo-floating-chat-confirm';
      panel.innerHTML = `
        <section class="card">
          <h1>Keep chatting in this window?</h1>
          <p>Your Kincmo conversation stays available while you switch tabs or use another app. Keep the original Kincmo tab open.</p>
          <div class="actions">
            <button type="button" data-keep>Keep chatting here</button>
            <button type="button" data-close>Close window</button>
          </div>
        </section>`;
      document.body.appendChild(panel);
      document.documentElement.classList.remove('kincmo-floating-chat-confirm-boot');
    }

    const keep = panel.querySelector('[data-kfc-keep], [data-keep]');
    const close = panel.querySelector('[data-kfc-close], [data-close]');
    const copy = panel.querySelector('p');

    function savedChatUrl() {
      const raw = new URLSearchParams(location.search).get(NEXT_PARAM);
      if (!raw) return popupUrl();
      try {
        const url = new URL(raw, location.href);
        if (url.origin !== location.origin) return popupUrl();
        url.searchParams.set(PARAM, '1');
        url.searchParams.delete(CONFIRM_PARAM);
        url.searchParams.delete(NEXT_PARAM);
        url.searchParams.delete('kincmo_floating_accepted');
        return url.href;
      } catch (_) {
        return popupUrl();
      }
    }

    function prewarmChat() {
      if (destroyed || confirmPrewarmReady) return;
      const hosts = launcher()?.getHosts?.() || Array.from(document.querySelectorAll('shopify-chat, shopify-agent'));
      const open = Boolean(launcher()?.isChatOpen?.()) || hosts.some((host) => launcher()?.isHostOpen?.(host));
      if (open) {
        confirmPrewarmReady = true;
        return;
      }
      clickOfficialChatLauncher();
      confirmPrewarmAttempts += 1;
      if (confirmPrewarmAttempts < 80) {
        confirmPrewarmTimer = window.setTimeout(prewarmChat, confirmPrewarmAttempts < 20 ? 60 : 120);
      }
    }

    let transitioning = false;
    function transitionToChat() {
      if (destroyed || transitioning) return;
      transitioning = true;
      keep.disabled = true;
      close.disabled = true;
      keep.textContent = confirmPrewarmReady ? 'Opening chat…' : 'Opening chat…';
      if (copy) copy.textContent = confirmPrewarmReady
        ? 'Your Kincmo conversation is ready.'
        : 'Finishing your Kincmo conversation…';

      const target = new URL(savedChatUrl());
      const relative = target.pathname + target.search + target.hash;

      // Stay on the already-loaded storefront document. Shopify Inbox has been
      // warming up invisibly behind the confirmation layer, so confirmation no
      // longer triggers a full page reload / second Inbox initialization.
      try { history.replaceState(history.state, '', relative); } catch (_) {}

      document.documentElement.classList.add('kincmo-floating-chat-boot');
      installPopupPresentation(confirmPrewarmReady);

      panel.remove();
      style?.remove();
      document.documentElement.classList.remove('kincmo-floating-chat-confirm-boot', 'kincmo-floating-chat-confirm-window');
      window.clearTimeout(confirmPrewarmTimer);
    }

    window.__kincmoFloatingConfirmKeep = transitionToChat;
    if (!earlyPanel) keep.addEventListener('click', transitionToChat);
    close?.addEventListener('click', () => window.close());

    // Begin loading/opening Shopify Inbox while the confirmation card remains
    // fully visible. The customer still decides whether to continue here, but
    // the expensive initialization happens before that click.
    confirmPrewarmTimer = window.setTimeout(prewarmChat, 0);

    if (window.__kincmoFloatingKeepRequested) {
      window.__kincmoFloatingKeepRequested = false;
      transitionToChat();
      return;
    }

    window.__kincmoInboxPip = {
      version: '21.10.2-confirm-prewarm',
      destroy() {
        destroyed = true;
        window.clearTimeout(confirmPrewarmTimer);
        try { delete window.__kincmoFloatingConfirmKeep; } catch (_) {}
        if (!earlyPanel) panel.remove();
        style?.remove();
      }
    };
  }

  function installDirectFloatingLauncher() {
    const CHANNEL_NAME = 'kincmo-document-pip-lifecycle';
    const AUTO_DELAY_MS = 300;
    const sourceId = (window.crypto?.randomUUID?.() || `kincmo-${Date.now()}-${Math.random()}`);

    let pipWindow = null;
    let requestPending = false;
    let requestSerial = 0;
    let hiddenTimer = 0;
    let pipClosedTimer = 0;
    let autoPaused = false;
    let floatingChatConfirmed = false;
    let activeFetchController = null;
    let channel = null;

    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch (_) {}

    function broadcast(type) {
      try { channel?.postMessage({ type, sourceId }); } catch (_) {}
    }

    function documentPipSupported() {
      return Boolean(window.documentPictureInPicture?.requestWindow);
    }

    function openIndependentWindow() {
      if (popup && !popup.closed) {
        try { popup.focus(); } catch (_) {}
        return true;
      }
      try {
        popup = window.open(popupUrl(), POPUP_NAME, popupFeatures());
      } catch (_) {
        popup = null;
      }
      if (!popup) return false;
      try { popup.focus(); } catch (_) {}
      return true;
    }

    function stopPipClosedWatch() {
      window.clearInterval(pipClosedTimer);
      pipClosedTimer = 0;
    }

    function cleanupPipState() {
      stopPipClosedWatch();
      pipWindow = null;
    }

    function closePip(reason = 'close') {
      const win = pipWindow;
      abortActiveFetch();
      cleanupPipState();
      if (win && !win.closed) {
        try { win.close(); } catch (_) {}
      }
      if (reason === 'user-close' && document.visibilityState === 'hidden') {
        autoPaused = true;
      }
      broadcast('pip-closed');
    }

    function watchPipClosed(win) {
      stopPipClosedWatch();
      pipClosedTimer = window.setInterval(() => {
        if (destroyed || pipWindow !== win) {
          stopPipClosedWatch();
          return;
        }
        let closed = false;
        try { closed = Boolean(win.closed); } catch (_) {}
        if (!closed) return;
        const userClosedWhileAway = document.visibilityState === 'hidden';
        abortActiveFetch();
        cleanupPipState();
        if (userClosedWhileAway) autoPaused = true;
        broadcast('pip-closed');
      }, 250);
    }

    function paintPipLoading(win, message = 'Opening Kincmo chat…') {
      try {
        const doc = win.document;
        doc.open();
        doc.write(`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Kincmo chat</title><style>html,body{margin:0;width:100%;height:100%;background:#fff;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}body{display:flex;align-items:center;justify-content:center;color:#6b625d;font-size:14px}.kincmo-pip-loading{padding:24px;text-align:center}</style></head><body><div class="kincmo-pip-loading">${message}</div></body></html>`);
        doc.close();
      } catch (_) {}
    }

    function paintPipConfirm(win, onKeep, onClose) {
      try {
        const doc = win.document;
        doc.open();
        doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Kincmo chat</title>
<style>
html,body{margin:0;width:100%;height:100%;overflow:hidden;background:#f6f6f6;color:#202020;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
body{display:flex;align-items:center;justify-content:center;padding:22px;box-sizing:border-box}
.card{width:min(100%,390px);box-sizing:border-box;background:#fff;border:1px solid #e9e5e1;border-radius:14px;padding:26px 22px;box-shadow:0 10px 28px rgba(0,0,0,.12)}
h1{margin:0 0 12px;font-size:20px;line-height:1.3;font-weight:700;letter-spacing:0;text-transform:none}
p{margin:0 0 22px;font-size:14px;line-height:1.5;color:#5e5752}
.actions{display:grid;gap:10px}
button{appearance:none;width:100%;min-height:46px;border-radius:9px;border:1px solid #ded7d2;font:600 14px/1 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;cursor:pointer}
button:disabled{cursor:default;opacity:.72}
.primary{background:#71351f;color:#fff;border-color:#71351f}.secondary{background:#ededed;color:#222}
</style>
</head>
<body>
<section class="card">
<h1>Keep chatting in this window?</h1>
<p>Your Kincmo conversation stays open while you switch tabs or apps.</p>
<div class="actions">
<button class="primary" type="button">Keep chatting here</button>
<button class="secondary" type="button">Close window</button>
</div>
</section>
</body>
</html>`);
        doc.close();
        const keep = doc.querySelector('.primary');
        const close = doc.querySelector('.secondary');
        let accepted = false;
        keep?.addEventListener('click', () => {
          if (accepted) return;
          accepted = true;
          keep.disabled = true;
          keep.textContent = 'Opening chat…';
          onKeep?.(keep);
        });
        close?.addEventListener('click', () => onClose?.());
      } catch (_) {}
    }

    function paintPipError(win, message, onRetry) {
      try {
        const safe = String(message).replace(/[&<>"']/g, (ch) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
        const doc = win.document;
        doc.open();
        doc.write(`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Kincmo chat</title><style>html,body{margin:0;width:100%;height:100%;background:#fff;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}body{display:flex;align-items:center;justify-content:center;color:#6b625d}.card{padding:24px;max-width:380px;text-align:center}.card p{margin:0 0 18px;line-height:1.5}.card button{appearance:none;min-height:42px;padding:0 20px;border:0;border-radius:9px;background:#71351f;color:#fff;font-weight:600;cursor:pointer}</style></head><body><div class="card"><p>${safe}</p>${onRetry ? '<button type="button">Try again</button>' : ''}</div></body></html>`);
        doc.close();
        if (onRetry) doc.querySelector('button')?.addEventListener('click', onRetry);
      } catch (_) {}
    }

    function prepareFetchedChatHtml(html, chatUrl) {
      const escapedBase = String(chatUrl).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      const bootstrap = `<base href="${escapedBase}"><script>window.__kincmoFetchedPipMode=true;document.documentElement.classList.add('kincmo-floating-chat-window','kincmo-floating-chat-boot');<\/script>`;
      if (/<head(?:\s[^>]*)?>/i.test(html)) {
        return html.replace(/<head(?:\s[^>]*)?>/i, (match) => `${match}${bootstrap}`);
      }
      if (/<html(?:\s[^>]*)?>/i.test(html)) {
        return html.replace(/<html(?:\s[^>]*)?>/i, (match) => `${match}<head>${bootstrap}</head>`);
      }
      return `<!doctype html><html><head>${bootstrap}</head><body>${html}</body></html>`;
    }

    function abortActiveFetch() {
      try { activeFetchController?.abort(); } catch (_) {}
      activeFetchController = null;
    }

    function fetchChatHtml(win, serial) {
      abortActiveFetch();
      const chatUrl = popupUrl();
      const controller = new AbortController();
      activeFetchController = controller;

      const promise = fetch(chatUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: { 'X-Kincmo-Floating-Chat': 'document-pip' }
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Chat page returned HTTP ${response.status}`);
        return prepareFetchedChatHtml(await response.text(), chatUrl);
      }).finally(() => {
        if (activeFetchController === controller) activeFetchController = null;
      });

      return { controller, promise };
    }

    function renderFetchedChat(win, serial, preparedHtml) {
      if (destroyed || serial !== requestSerial || pipWindow !== win || win.closed) return false;
      win.__kincmoFetchedPipMode = true;
      const doc = win.document;
      doc.open();
      doc.write(preparedHtml);
      doc.close();
      try { win.focus(); } catch (_) {}
      return true;
    }

    function injectConfirmOverlay(preparedHtml) {
      const style = `<style id="kincmo-pip-confirm-overlay-style">
#kincmo-pip-confirm-overlay{position:fixed!important;inset:0!important;z-index:2147483647!important;display:flex!important;align-items:center!important;justify-content:center!important;padding:22px!important;box-sizing:border-box!important;background:#f6f6f6!important;color:#202020!important;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;visibility:visible!important;opacity:1!important}
#kincmo-pip-confirm-overlay *{box-sizing:border-box!important;visibility:visible!important}
#kincmo-pip-confirm-overlay .card{width:min(100%,390px)!important;background:#fff!important;border:1px solid #e9e5e1!important;border-radius:14px!important;padding:26px 22px!important;box-shadow:0 10px 28px rgba(0,0,0,.12)!important}
#kincmo-pip-confirm-overlay h1{margin:0 0 12px!important;font:700 20px/1.3 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;letter-spacing:0!important;text-transform:none!important;color:#202020!important}
#kincmo-pip-confirm-overlay p{margin:0 0 22px!important;font:400 14px/1.5 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;color:#5e5752!important}
#kincmo-pip-confirm-overlay .actions{display:grid!important;gap:10px!important}
#kincmo-pip-confirm-overlay button{appearance:none!important;width:100%!important;min-height:46px!important;border-radius:9px!important;border:1px solid #ded7d2!important;font:600 14px/1 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;cursor:pointer!important}
#kincmo-pip-confirm-overlay button:disabled{cursor:default!important;opacity:.72!important}
#kincmo-pip-confirm-overlay .primary{background:#71351f!important;color:#fff!important;border-color:#71351f!important}
#kincmo-pip-confirm-overlay .secondary{background:#ededed!important;color:#222!important}
</style>`;
      const markup = `<div id="kincmo-pip-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="kincmo-pip-confirm-title">
<section class="card"><h1 id="kincmo-pip-confirm-title">Keep chatting in this window?</h1><p>Your Kincmo conversation stays open while you switch tabs or apps.</p><div class="actions"><button class="primary" type="button">Keep chatting here</button><button class="secondary" type="button">Close window</button></div></section>
</div>`;

      let html = preparedHtml;
      if (/<\/head>/i.test(html)) html = html.replace(/<\/head>/i, `${style}</head>`);
      else html = style + html;
      if (/<body(?:\s[^>]*)?>/i.test(html)) html = html.replace(/<body(?:\s[^>]*)?>/i, (match) => `${match}${markup}`);
      else html = markup + html;
      return html;
    }

    function chatReadyInPip(win) {
      try {
        return win.document.documentElement.classList.contains('kincmo-floating-chat-ready');
      } catch (_) {
        return false;
      }
    }

    function removeConfirmOverlayWhenReady(win, serial, state) {
      if (!state.accepted) return;
      const check = () => {
        if (destroyed || serial !== requestSerial || pipWindow !== win || win.closed) return;
        const overlay = win.document.getElementById('kincmo-pip-confirm-overlay');
        if (!overlay) return;
        if (chatReadyInPip(win)) {
          overlay.remove();
          win.document.getElementById('kincmo-pip-confirm-overlay-style')?.remove();
          try { win.focus(); } catch (_) {}
          return;
        }
        win.setTimeout(check, 40);
      };
      check();
    }

    function renderPreinitializedChatWithConfirm(win, serial, preparedHtml, state) {
      if (destroyed || serial !== requestSerial || pipWindow !== win || win.closed) return false;
      win.__kincmoFetchedPipMode = true;
      const doc = win.document;
      doc.open();
      doc.write(injectConfirmOverlay(preparedHtml));
      doc.close();

      const overlay = doc.getElementById('kincmo-pip-confirm-overlay');
      const keep = overlay?.querySelector('.primary');
      const close = overlay?.querySelector('.secondary');

      const syncAcceptedUi = () => {
        if (!keep || !state.accepted) return;
        keep.disabled = true;
        keep.setAttribute('aria-busy', 'true');
        keep.textContent = 'Opening chat…';
      };

      keep?.addEventListener('click', () => {
        if (state.accepted) return;
        state.accepted = true;
        floatingChatConfirmed = true;
        syncAcceptedUi();
        removeConfirmOverlayWhenReady(win, serial, state);
      });
      close?.addEventListener('click', () => closePip('user-close'));

      syncAcceptedUi();
      removeConfirmOverlayWhenReady(win, serial, state);
      try { win.focus(); } catch (_) {}
      return true;
    }

    function beginChatLoad(win, serial, { showLoading = false } = {}) {
      if (showLoading) paintPipLoading(win);
      const preload = fetchChatHtml(win, serial);
      preload.promise.then((preparedHtml) => {
        renderFetchedChat(win, serial, preparedHtml);
      }).catch((error) => {
        if (error?.name === 'AbortError') return;
        if (destroyed || serial !== requestSerial || pipWindow !== win || win.closed) return;
        paintPipError(
          win,
          `Could not load Kincmo chat: ${error?.message || 'Unknown error'}`,
          () => beginChatLoad(win, serial, { showLoading: true })
        );
      });
      return preload;
    }

    function beginConfirmedFlow(win, serial) {
      paintPipLoading(win);
      beginChatLoad(win, serial);
    }

    function beginFirstTimeConfirmFlow(win, serial) {
      // Keep the confirmation page completely stable until the customer makes
      // an explicit choice. We still fetch/prepare the chat HTML immediately so
      // network time is hidden behind the decision, but we do NOT write it into
      // the PiP document before acceptance. That prevents the confirmation card
      // from flashing/disappearing when the prefetched document becomes ready.
      const preload = fetchChatHtml(win, serial);
      let preparedHtml = null;
      let preloadError = null;
      let accepted = false;

      preload.promise.then((html) => {
        if (destroyed || serial !== requestSerial || pipWindow !== win || win.closed) return;
        preparedHtml = html;
        if (accepted) renderFetchedChat(win, serial, preparedHtml);
      }).catch((error) => {
        if (error?.name === 'AbortError') return;
        preloadError = error;
        if (accepted && !destroyed && serial === requestSerial && pipWindow === win && !win.closed) {
          paintPipError(
            win,
            `Could not load Kincmo chat: ${error?.message || 'Unknown error'}`,
            () => beginChatLoad(win, serial, { showLoading: true })
          );
        }
      });

      paintPipConfirm(
        win,
        () => {
          if (destroyed || serial !== requestSerial || pipWindow !== win || win.closed || accepted) return;
          accepted = true;
          floatingChatConfirmed = true;

          if (preparedHtml) {
            renderFetchedChat(win, serial, preparedHtml);
            return;
          }

          if (preloadError) {
            paintPipError(
              win,
              `Could not load Kincmo chat: ${preloadError?.message || 'Unknown error'}`,
              () => beginChatLoad(win, serial, { showLoading: true })
            );
          }
          // If fetch is still in flight, paintPipConfirm keeps the same card on
          // screen and changes only the primary button to "Opening chat…". The
          // actual chat document is written only after this explicit acceptance.
        },
        () => closePip('user-close')
      );
    }

    async function finishDocumentPipOpen(requestPromise, serial, automatic) {
      try {
        const win = await requestPromise;
        if (serial === requestSerial) requestPending = false;

        if (destroyed || serial !== requestSerial ||
            (automatic && document.visibilityState === 'visible')) {
          try { win.close(); } catch (_) {}
          return;
        }

        pipWindow = win;
        broadcast('pip-opened');
        watchPipClosed(win);

        if (floatingChatConfirmed) beginConfirmedFlow(win, serial);
        else beginFirstTimeConfirmFlow(win, serial);
      } catch (error) {
        if (serial !== requestSerial || destroyed) return;
        requestPending = false;
        // Automatic PiP may be blocked after transient user activation expires.
        // Keep the storefront chat usable and do not show the removed left panel.
        if (!automatic && !documentPipSupported()) openIndependentWindow();
      }
    }

    function requestDocumentPip({ automatic = false } = {}) {
      if (destroyed || !desktop() || requestPending) return false;
      if (pipWindow && !pipWindow.closed) {
        try { pipWindow.focus(); } catch (_) {}
        return true;
      }
      if (!launcher()?.isChatOpen?.()) return false;

      if (!documentPipSupported()) {
        if (!automatic) return openIndependentWindow();
        return false;
      }

      const serial = ++requestSerial;
      requestPending = true;
      let requestPromise;
      try {
        requestPromise = window.documentPictureInPicture.requestWindow({
          width: POPUP_WIDTH,
          height: POPUP_HEIGHT
        });
      } catch (_) {
        requestPending = false;
        if (!automatic) openIndependentWindow();
        return false;
      }

      finishDocumentPipOpen(requestPromise, serial, automatic);
      return true;
    }

    function scheduleAutomaticAttempt() {
      window.clearTimeout(hiddenTimer);
      if (document.visibilityState !== 'hidden' || autoPaused || destroyed) return;
      if (!launcher()?.isChatOpen?.() || pipWindow || requestPending) return;

      hiddenTimer = window.setTimeout(() => {
        if (destroyed || document.visibilityState !== 'hidden' || autoPaused) return;
        if (!launcher()?.isChatOpen?.() || pipWindow || requestPending) return;
        requestDocumentPip({ automatic: true });
      }, AUTO_DELAY_MS);
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        scheduleAutomaticAttempt();
        return;
      }

      window.clearTimeout(hiddenTimer);
      ++requestSerial;
      requestPending = false;
      abortActiveFetch();
      autoPaused = false;
      if (pipWindow && !pipWindow.closed) closePip('storefront-return');
      broadcast('storefront-active');
      try {
        const lifecycleChannel = new BroadcastChannel('kincmo-floating-lifecycle');
        lifecycleChannel.postMessage({ type: 'storefront-active' });
        lifecycleChannel.close();
      } catch (_) {}
    }

    function onInboxState() {
      const chatOpen = Boolean(launcher()?.isChatOpen?.());
      if (!chatOpen) {
        window.clearTimeout(hiddenTimer);
        ++requestSerial;
        requestPending = false;
        abortActiveFetch();
        autoPaused = false;
        if (pipWindow && !pipWindow.closed) closePip('chat-closed');
      } else if (document.visibilityState === 'hidden') {
        scheduleAutomaticAttempt();
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('kincmo:inbox-state', onInboxState);
    window.addEventListener('pageshow', onVisibilityChange);

    window.__kincmoOpenFloatingChat = () => requestDocumentPip({ automatic: false });
    window.__kincmoFloatingLaunchWasRecent = () => false;

    window.__kincmoInboxPip = {
      version: '31.0-confirm-overlay-preinitialize-document-pip',
      destroy() {
        destroyed = true;
        window.clearTimeout(hiddenTimer);
        stopPipClosedWatch();
        ++requestSerial;
        requestPending = false;
        abortActiveFetch();
        try { if (pipWindow && !pipWindow.closed) pipWindow.close(); } catch (_) {}
        cleanupPipState();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        document.removeEventListener('kincmo:inbox-state', onInboxState);
        window.removeEventListener('pageshow', onVisibilityChange);
        channel?.close();
        try { delete window.__kincmoOpenFloatingChat; } catch (_) {}
        try { delete window.__kincmoFloatingLaunchWasRecent; } catch (_) {}
      }
    };
  }

  function installSourcePanel() {
    // Keep auxiliary panel styles with the code that owns the panel.
    const panelStyle = document.createElement('style');
    panelStyle.id = 'kincmo-inbox-pip-panel-style';
    panelStyle.textContent = `#kincmo-inbox-pip {
  position: fixed;
  left: 20px;
  bottom: max(20px, env(safe-area-inset-bottom));
  z-index: 2147483645;
  width: min(300px, calc(100vw - 40px));
  padding: 16px 34px 14px 16px;
  border: 1px solid #ded5cf;
  border-radius: 12px;
  background: #fff;
  color: #332722;
  box-shadow: 0 5px 24px #0002;
  font: 14px/1.45 system-ui, sans-serif;
}
#kincmo-inbox-pip[hidden], #kincmo-inbox-pip [hidden] { display: none !important; }
#kincmo-inbox-pip button { font: inherit; cursor: pointer; }
#kincmo-inbox-pip-open {
  border: 0;
  border-radius: 7px;
  padding: 10px 14px;
  background: #5f291e;
  color: #fff;
}
#kincmo-inbox-pip button:focus-visible { outline: 3px solid #b68b65; outline-offset: 3px; }
#kincmo-inbox-pip-open:disabled { opacity: .6; cursor: wait; }
#kincmo-inbox-pip p { margin: 9px 0 0; font: inherit; }
#kincmo-inbox-pip [data-pip-dismiss] {
  position: absolute; top: 4px; right: 4px; width: 28px; height: 28px;
  border: 0; background: transparent; color: #5f291e; font-size: 21px;
}
@media (max-width: 749px), (pointer: coarse) { #kincmo-inbox-pip { display: none !important; } }
`;
    document.head.appendChild(panelStyle);

    const panel = document.createElement('aside');
    panel.id = 'kincmo-inbox-pip';
    panel.setAttribute('aria-label', 'Continue your Kincmo chat');
    panel.innerHTML = '<button type="button" id="kincmo-inbox-pip-open" hidden>Open floating chat</button>' +
      '<p role="status" aria-live="polite"></p>' +
      '<button type="button" data-pip-dismiss aria-label="Dismiss floating chat tip">×</button>';

    const button = panel.querySelector('#kincmo-inbox-pip-open');
    const status = panel.querySelector('[role="status"]');
    const dismiss = panel.querySelector('[data-pip-dismiss]');
    document.body.appendChild(panel);

    let returnChannel = null;
    try { returnChannel = new BroadcastChannel('kincmo-floating-lifecycle'); } catch (_) {}
    function onStorefrontActive() {
      // A visible source page behind a focused popup is NOT a return to site.
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
      if (popup && !popup.closed) popup.close();
      try { returnChannel?.postMessage({ type: 'storefront-active' }); } catch (_) {}
      sync();
    }

    function sync() {
      if (destroyed) return;
      const chatOpen = Boolean(launcher()?.isChatOpen?.());
      const popupOpen = Boolean(popup && !popup.closed);

      panel.hidden = dismissed || !desktop() || (!chatOpen && !popupOpen);
      button.hidden = !chatOpen && !popupOpen;
      button.textContent = popupOpen ? 'Focus floating chat' : 'Open floating chat';
      status.textContent = popupOpen
        ? 'Your floating chat window is open.'
        : 'Open a separate chat window before switching tabs.';
    }

    function openPopup() {
      if (popup && !popup.closed) {
        popup.focus();
        sync();
        return;
      }
      if (!launcher()?.isChatOpen?.()) return;

      const confirmUrl = popupConfirmUrl();

      // Create the popup synchronously so the browser accepts it, paint the
      // confirmation UI immediately into the initial about:blank document,
      // then start the real Kincmo navigation almost at once. This removes the
      // visible white gap while the first storefront HTML response is pending.
      popup = window.open('', POPUP_NAME, popupFeatures());
      if (!popup) {
        status.textContent = 'Your browser blocked the floating chat window. Allow pop-ups for kincmo.com and try again.';
        panel.hidden = false;
        return;
      }

      // Carry consent across the SAME navigation. Changing the destination
      // after a click would cancel/restart a slow Shopify document request.
      let accepted = false;
      window.__kincmoFloatingWasConfirmed = (candidate) => candidate === popup && accepted;
      try { popup.sessionStorage.removeItem('kincmo-floating-confirmed'); } catch (_) {}
      paintInstantConfirmShell(popup, () => {
        accepted = true;
        try { popup.sessionStorage.setItem('kincmo-floating-confirmed', '1'); } catch (_) {}
      });
      popup.focus();
      try { popup.location.replace(confirmUrl); } catch (_) {
        try { popup.location.href = confirmUrl; } catch (_) {}
      }

      sync();
    }

    button.addEventListener('click', openPopup);
    dismiss.addEventListener('click', () => {
      dismissed = true;
      sync();
    });

    document.addEventListener('kincmo:inbox-state', sync);
    window.addEventListener('focus', onStorefrontActive);
    window.addEventListener('pageshow', onStorefrontActive);
    document.addEventListener('visibilitychange', onStorefrontActive);
    const timer = window.setInterval(sync, 1000);

    window.__kincmoInboxPip = {
      version: '21.10.0-popup',
      destroy() {
        destroyed = true;
        window.clearInterval(timer);
        document.removeEventListener('kincmo:inbox-state', sync);
        window.removeEventListener('focus', onStorefrontActive);
        window.removeEventListener('pageshow', onStorefrontActive);
        document.removeEventListener('visibilitychange', onStorefrontActive);
        returnChannel?.close();
        panel.remove();
        panelStyle.remove();
      }
    };

    sync();
  }

  const runWhenReady = (fn) => {
    if ((isConfirmMode || isPopupMode) && document.body && launcher()) {
      fn();
    } else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  if (isConfirmMode) {
    runWhenReady(() => { installFloatingLifecycle(); installConfirmPage(); });
  } else if (isPopupMode) {
    runWhenReady(() => { installFloatingLifecycle(); installPopupPresentation(); });
  } else {
    runWhenReady(() => {
      if (desktop()) installDirectFloatingLauncher();
      else installSourcePanel();
    });
  }
})();
